import r from '../Utils/fetch'

const w: any = window
const endPoint = `${w.API_PARTNERS_URL}`

export interface IPartner {
	id: string
	name: string
	legalName?: string
	city?: string
	country?: string
	status?: number
}

// Type d'une AFFAIRE (attribution), pas d'un partenaire : c'est l'affaire qui porte son
// mode de commissionnement et son taux. `leads` s'arrête au bout d'un an, `partner` au
// bout de deux — ancrés sur le début de l'affaire si l'admin l'associe à la main, sur la
// création du BA si elle naît d'un lien.
export const DEAL_TYPES = [
	{ id: 'leads', name: "Apport d'affaire" },
	{ id: 'partner', name: 'Partner' },
] as const

// Valeurs d'avant le renommage (2026-08-27). Uniquement pour l'AFFICHAGE de lignes non
// encore migrées — jamais proposées à la saisie.
export const LEGACY_DEAL_TYPE_LABELS: Record<string, string> = {
	apport: "Apport d'affaire",
	integrateur: 'Partner',
}

export interface ICommissionRate {
	id: number
	commissionType: string
	rate: number
}

export interface IAttribution {
	id: number
	partnerId: string
	billingAccountId: string
	opportunityId?: number
	status: string
	commissionType?: string
	rate?: number
	startAt?: string
	endAt?: string
	opportunity?: { id: number; companyName: string }
	partner?: { id: string; name: string }
}

// Recherche de partenaires par nom (filtre `q`, ILIKE côté backend).
export const searchPartners = (q: string): Promise<IPartner[]> =>
	r({
		url: `${endPoint}/v1/admin/partners?filter=${encodeURIComponent(JSON.stringify({ q }))}&range=${encodeURIComponent('[0,24]')}`,
		method: 'GET',
	})

// Attributions existantes pour un BA (association BA ↔ partenaire).
export const listAttributionsByBA = (billingAccountId: string): Promise<IAttribution[]> =>
	r({
		url: `${endPoint}/v1/admin/attributions?filter=${encodeURIComponent(JSON.stringify({ billing_account_id: billingAccountId }))}`,
		method: 'GET',
	})

// Grille des taux par défaut : une ligne par type d'affaire.
export const listCommissionRates = (): Promise<ICommissionRate[]> =>
	r({
		url: `${endPoint}/v1/admin/commission-rates`,
		method: 'GET',
	})

// Associe un partenaire au BA (crée une attribution partenaire ↔ BA, sans opportunité).
//
// ⚠️ `commissionType` et `rate` ne sont PAS optionnels en pratique : ce sont les termes de
// l'affaire, et c'est `rate` qui sera appliqué à chaque règlement du client. L'API les
// résout par défaut si on ne les envoie pas (type `apport`, taux de la grille), mais un
// appel muet laisserait l'admin croire qu'il n'a rien à décider.
export const associatePartner = (payload: {
	partnerId: string
	billingAccountId: string
	commissionType: string
	rate: number
	// AAAA-MM-JJ. Absent = aujourd'hui côté API.
	startAt?: string
}): Promise<IAttribution> =>
	r({
		url: `${endPoint}/v1/admin/attributions`,
		method: 'POST',
		data: payload,
	})

// Édite les TERMES d'une affaire : taux appliqué, début et fin de commissionnement.
//
// ⚠️ `rate` retarife les règlements À VENIR seulement. Les lignes de commission déjà
// enregistrées portent leur propre taux, figé au moment du règlement — les rejouer serait
// réécrire de l'argent déjà annoncé au partenaire.
//
// `commissionEndAt` a TROIS états côté API : absent = inchangé, chaîne vide = pas de fin
// (NULL, le cas d'une affaire `partner`), date = fin explicite.
export const updateAttributionTerms = (
	id: number,
	payload: { rate?: number; startAt?: string; commissionEndAt?: string }
): Promise<IAttribution> =>
	r({
		url: `${endPoint}/v1/admin/attributions/${id}`,
		method: 'PUT',
		data: payload,
	})

// Retouche d'une ligne de commission non réglée : taux négocié, forçage, motif (obligatoire).
// Le montant est recalculé côté API depuis l'encaissé ; 409 si la ligne est déjà dans un
// versement.
export const updateCommissionLine = (
	id: number,
	payload: { rate: number; forced: boolean; note: string }
): Promise<unknown> =>
	r({
		url: `${endPoint}/v1/admin/commissions/${id}`,
		method: 'PUT',
		data: payload,
	})

// Suppression réelle d'une ligne non réglée (409 sinon). Le recalcul la recréera si la
// facture est dans la fenêtre de l'affaire.
export const deleteCommissionLine = (id: number): Promise<unknown> =>
	r({ url: `${endPoint}/v1/admin/commissions/${id}`, method: 'DELETE' })

// Clôture une attribution (le client quitte ce partenaire).
//
// RÈGLE MÉTIER : un client n'appartient qu'à UN partenaire à la fois. Transférer un
// client se fait donc en DEUX gestes — clôturer ici, puis associer le nouveau. L'API
// refuse la seconde association tant que la première est active, exprès : basculer
// automatiquement retirerait un client (et ses commissions) à un partenaire sur un
// clic de trop.
//
// On n'envoie que `status` et `endAt` : côté API le PATCH ignore les valeurs nulles du
// type, donc les autres champs (taux, dates de commissionnement) restent intacts.
export const endAttribution = (id: number): Promise<IAttribution> =>
	r({
		url: `${endPoint}/v1/admin/attributions/${id}`,
		method: 'PUT',
		data: { status: 'ended', endAt: new Date().toISOString() },
	})

// #region Wallet
export interface IWalletBalances {
	spendable: number
	retirable: number
	overdraftLimit: number
	commissionOwed: number
	maturedAvailable: number
	creditsGranted: number
	debits: number
}
export interface IWalletEntry {
	id: number
	kind: string
	category: string
	amount: number
	label: string
	createdBy?: string
	createdAt: string
	editedBy?: string
	editedAt?: string
}

// Seules les lignes saisies par un admin se retouchent ou s'annulent (409 sinon) : une
// consommation de feature est un débit du système.
export const isWalletEntryEditable = (e: IWalletEntry) => e.category === 'granted' || e.category === 'adjustment'

// Soldes unifiés + historique du wallet d'un partenaire.
export const getPartnerWallet = (id: string): Promise<{ balances: IWalletBalances; entries: IWalletEntry[] }> =>
	r({ url: `${endPoint}/v1/admin/partners/${id}/wallet`, method: 'GET' })

// Ajoute une ligne de wallet (amount > 0 = crédit offert, < 0 = ajustement débit).
export const addPartnerWalletEntry = (id: string, amount: number, label: string): Promise<IWalletEntry> =>
	r({ url: `${endPoint}/v1/admin/partners/${id}/wallet/entries`, method: 'POST', data: { amount, label } })

// Retouche d'une ligne admin (montant signé, comme à la création).
export const updatePartnerWalletEntry = (id: string, entryId: number, amount: number, label: string): Promise<IWalletEntry> =>
	r({ url: `${endPoint}/v1/admin/partners/${id}/wallet/entries/${entryId}`, method: 'PUT', data: { amount, label } })

// Annulation d'une ligne admin : elle sort des soldes, mais reste en base avec son auteur.
export const deletePartnerWalletEntry = (id: string, entryId: number): Promise<unknown> =>
	r({ url: `${endPoint}/v1/admin/partners/${id}/wallet/entries/${entryId}`, method: 'DELETE' })

export interface IRecomputeLine {
	attributionId: number
	billingAccountId: string
	invoiceId: string
	collectedAt: string
	collectedAmount: number
	rate: number
	amount: number
}

export interface IRecomputeReport {
	applied: boolean
	lines: IRecomputeLine[]
	total: number
	alreadyCommissioned: number
	outOfWindow: number
	existingOutOfWindow: number
}

// Rejoue les ENCAISSEMENTS passés des clients du partenaire (paiements Stripe, factures
// Pennylane payées) et crée les commissions MANQUANTES. `apply: false` = simulation, rien n'est écrit. N'ajoute jamais que des
// lignes : rien n'est supprimé ni retarifé.
export const recomputePartnerCommissions = (id: string, apply: boolean): Promise<IRecomputeReport> =>
	r({ url: `${endPoint}/v1/admin/partners/${id}/commissions/recompute`, method: 'POST', data: { apply } })
// #endregion

// #region Membres d'une organisation partenaire
export interface IPartnerMemberInvite {
	partnerId: string
	email: string
	firstName?: string
	lastName?: string
	role?: string
}

// Le membre créé, plus `emailSent` : l'email d'invitation est best-effort côté API, et
// l'appelant doit pouvoir distinguer « invitation partie » de « membre ajouté en silence ».
export interface IPartnerMemberCreated {
	id: number
	email: string
	role: string
	emailSent: boolean
}

// Invite un utilisateur dans une organisation partenaire.
// Appel direct plutôt que le dataProvider react-admin : ra-data-simple-rest ne conserve du
// corps de réponse que `id` (il recompose le reste depuis ce qu'on a envoyé), donc
// `emailSent` serait perdu en route.
export const invitePartnerMember = (payload: IPartnerMemberInvite): Promise<IPartnerMemberCreated> =>
	r({ url: `${endPoint}/v1/admin/partner-members`, method: 'POST', data: payload })
// #endregion

// #region Demandes d'accès à l'espace partenaire
export interface ISignupRequest {
	id: number
	email: string
	firstName: string
	lastName: string
	phone: string
	country: string
	companyName: string
	emailVerifiedAt: string
	createdAt: string
	hasQuantiAccount: boolean
}

// Approuve une demande : crée l'organisation + son propriétaire, et supprime la demande.
//
// ⚠️ Pas un PUT de statut : il n'y a pas d'état « approuvée » à porter. La demande
// disparaît des deux côtés de la décision, seul ce qu'elle produit subsiste.
export const approveSignupRequest = (id: number): Promise<{ id: string; name: string }> =>
	r({ url: `${endPoint}/v1/admin/partner-signup-requests/${id}/approve`, method: 'POST' })

// Rejette une demande : suppression définitive, sans notification au demandeur.
export const rejectSignupRequest = (id: number): Promise<{ id: number }> =>
	r({ url: `${endPoint}/v1/admin/partner-signup-requests/${id}`, method: 'DELETE' })
// #endregion

// #region Contexte partenaire d'un UTILISATEUR (fiche admin Users)
export interface IUserPartnerMembership {
	id: number
	partnerId: string
	partnerName: string
	role: string
	userId: number
	email: string
	roleInOrg?: string
}

export interface IUserSignupRequest {
	id: number
	email: string
	firstName: string
	lastName: string
	companyName: string
	phone?: string
	country?: string
	/** Une demande non vérifiée est INVISIBLE de l'écran d'arbitrage (cf. l'API). */
	verified: boolean
	createdAt: string
}

export interface IUserPartnerContext {
	memberships: IUserPartnerMembership[]
	signupRequest: IUserSignupRequest | null
}

// Tout se résout par EMAIL : une appartenance posée par invitation porte `userId = 0`
// tant qu'elle n'est pas acceptée, et c'est justement celle-là qu'on veut voir ici —
// elle explique pourquoi un compte paraît sans accès alors qu'un accès l'attend.
export const getUserPartnerContext = (email: string): Promise<IUserPartnerContext> =>
	r({
		url: `${endPoint}/v1/admin/user-partner-context`,
		method: 'GET',
		data: { email },
	})
// #endregion
